import React from "react";
import { Typography, Box } from "@mui/material";
import variableStyles from "../../theme/variable-styles";

interface Participant {
  id: string;
  fullName: string;
  userName?: string;
}

interface RenderMentionsProps {
  isMe: boolean;
  content: string;
  participants: Participant[];
}

/**
 * RenderMentions v7 — ES5-safe, match đúng theo participants
 * - Chỉ highlight @ + tên tồn tại trong participants (fullName / userName)
 * - Ưu tiên match dài nhất (tránh cắt giữa “Nguyen Van” vs “Nguyen”)
 * - Không dính text phía sau (kiểm tra boundary)
 */
const RenderMentions: React.FC<RenderMentionsProps> = ({
  isMe,
  content,
  participants
}) => {
  if (!content) return null;

  // Chuẩn hoá so sánh không phân biệt hoa/thường và có thể bỏ dấu
  const safeNorm = (s: string) => {
    if (!s) return "";
    const lower = s.toLowerCase();
    if ((lower as any).normalize) {
      return lower.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    return lower;
  };

  // Tập ứng viên từ participants (loại rỗng, trùng), sắp xếp theo độ dài giảm dần
  const candidateSet: Record<string, { raw: string; from: "full" | "user" }> =
    {};
  participants.forEach(p => {
    if (p.fullName) {
      const key = safeNorm(p.fullName.trim());
      if (key) candidateSet[key] = { raw: p.fullName.trim(), from: "full" };
    }
    if (p.userName) {
      const key = safeNorm(p.userName.trim());
      if (key) candidateSet[key] = { raw: p.userName.trim(), from: "user" };
    }
  });
  const candidates = Object.keys(candidateSet)
    .map(k => ({ key: k, raw: candidateSet[k].raw }))
    .sort((a, b) => b.raw.length - a.raw.length); // ưu tiên dài nhất

  // boundary: kết thúc hợp lệ (hết chuỗi, space, dấu câu,…)
  const isBoundary = (ch?: string) =>
    !ch || /[\s.,!?;:()"'`[\]{}<>/\n\r\t]/.test(ch);

  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < content.length) {
    const ch = content.charAt(i);

    if (ch !== "@") {
      // đẩy từng đoạn thường cho đến khi gặp '@' để tránh nhiều re-render nhỏ
      const nextAt = content.indexOf("@", i);
      const end = nextAt === -1 ? content.length : nextAt;
      nodes.push(<span key={`t-${i}`}>{content.slice(i, end)}</span>);
      i = end;
      continue;
    }

    // gặp '@' → thử khớp ứng viên dài nhất
    const afterAtIndex = i + 1;
    const remaining = content.slice(afterAtIndex);
    const remainingNorm = safeNorm(remaining);

    let matchedLen = 0;
    let matchedRaw = "";

    for (let c = 0; c < candidates.length; c++) {
      const cand = candidates[c];
      const rawLen = cand.raw.length;

      // đủ độ dài?
      if (remaining.length < rawLen) continue;

      const segRaw = remaining.slice(0, rawLen);
      if (safeNorm(segRaw) === cand.key) {
        // kiểm tra boundary sau tên
        const boundaryChar = content.charAt(afterAtIndex + rawLen);
        if (isBoundary(boundaryChar)) {
          matchedLen = rawLen;
          matchedRaw = segRaw; // giữ nguyên đúng user gõ (giữ dấu, hoa/thường)
          break; // vì đã sort dài -> trúng cái dài nhất đầu tiên
        }
      }
    }

    if (matchedLen > 0) {
      // có match → push phần mention
      nodes.push(
        <Box
          key={`m-${i}`}
          component="span"
          sx={{
            // color: "#000000",
            fontWeight: 600,
            cursor: "pointer",
            display: "inline",
            "&:hover": { textDecoration: "underline" }
          }}
          title={matchedRaw}
        >
          @{matchedRaw}
        </Box>
      );
      i = afterAtIndex + matchedLen; // nhảy qua cả mention
    } else {
      // không match: '@' là ký tự thường
      nodes.push(<span key={`at-${i}`}>@</span>);
      i += 1;
    }
  }

  return (
    <Typography
      component="span"
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14 }}
    >
      {nodes}
    </Typography>
  );
};

export default RenderMentions;
